import { supabaseUrl, supabaseAnonKey, refreshSession, safeGetSession } from '../supabase/config';

// Default request timeout (30 seconds)
const DEFAULT_TIMEOUT = 30000;

/**
 * Helper to get the current authentication token
 * Uses Supabase SDK's getSession() which automatically refreshes expired tokens
 */
const getAuthToken = async () => {
    try {
        const { data: { session }, error } = await safeGetSession();
        if (!error && session?.access_token) {
            return session.access_token;
        }
    } catch (e) {
        console.warn('Failed to get auth token via SDK:', e);
    }
    // Fallback: read directly from localStorage (critical for mobile)
    return getAuthTokenSync();
};

/**
 * Synchronous fallback for getting token from localStorage (used when async is not possible)
 */
const getAuthTokenSync = () => {
    try {
        const storageKey = 'instacredmoney-auth';
        const sessionStr = window.localStorage.getItem(storageKey);
        if (sessionStr) {
            const sessionData = JSON.parse(sessionStr);
            return sessionData.access_token || sessionData.session?.access_token || null;
        }
    } catch (e) {
        console.warn('Failed to read session from local storage:', e);
    }
    return null;
};

/**
 * Fetch with timeout wrapper
 */
const fetchWithTimeout = async (url, options, timeout = DEFAULT_TIMEOUT) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
        const response = await fetch(url, {
            ...options,
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            throw new Error('Request timed out. Please check your connection and try again.');
        }
        throw error;
    }
};

/**
 * Universal manual fetch for Supabase REST API
 * Bypasses the SDK to avoid WebSocket/Auth timeout issues
 * Features:
 * - Automatic token refresh before requests
 * - Request timeout to prevent hanging
 * - Auto-retry on 401 with token refresh
 *
 * @param {string} endpoint - The API endpoint (e.g., 'leads', 'leads?select=*')
 * @param {Object} options - Fetch options (method, body, headers)
 * @param {boolean} isRetry - Internal flag to prevent infinite retry loops
 * @returns {Promise<any>} - JSON response
 */
export const supabaseFetch = async (endpoint, options = {}, isRetry = false) => {
    // Determine the base URL
    // If endpoint starts with http, use it as is, otherwise append to supabase REST URL
    let url = endpoint;
    if (!endpoint.startsWith('http')) {
        // Remove leading slash if present to avoid double slashes
        const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;

        // Handle auth endpoints separately vs rest/v1
        if (cleanEndpoint.startsWith('auth/')) {
            url = `${supabaseUrl}/${cleanEndpoint}`;
        } else {
            url = `${supabaseUrl}/rest/v1/${cleanEndpoint}`;
        }
    }

    // Get a fresh token (this will auto-refresh if expired)
    const token = await getAuthToken();
    const apiKey = supabaseAnonKey;

    const defaultHeaders = {
        'apikey': apiKey,
        'Authorization': token ? `Bearer ${token}` : `Bearer ${apiKey}`,
        'Content-Type': 'application/json'
    };

    // Merge headers
    const headers = {
        ...defaultHeaders,
        ...(options.headers || {})
    };

    const fetchOptions = {
        ...options,
        headers
    };

    try {
        const response = await fetchWithTimeout(url, fetchOptions);

        // Handle 401 Unauthorized - try to refresh token and retry once
        if (response.status === 401 && !isRetry) {
            console.warn('supabaseFetch: Got 401, attempting token refresh and retry...');
            const refreshed = await refreshSession();
            if (refreshed) {
                // Retry the request with fresh token
                return supabaseFetch(endpoint, options, true);
            } else {
                // Session couldn't be refreshed - user needs to login again
                const error = new Error('Session expired. Please login again.');
                error.status = 401;
                error.code = 'SESSION_EXPIRED';
                throw error;
            }
        }

        if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}));
            // Throw an object compatible with Supabase SDK error structure if possible
            const error = new Error(errorBody.message || errorBody.error_description || `Request failed: ${response.status}`);
            error.status = response.status;
            error.code = errorBody.code;
            error.details = errorBody;
            throw error;
        }

        // Handle 204 No Content (common for deletions)
        if (response.status === 204) {
            return null;
        }

        const text = await response.text();
        // If body is empty or just whitespace, return null instead of crashing on JSON parse
        const trimmedText = text ? text.trim() : '';
        if (!trimmedText) {
            return null;
        }
        try {
            return JSON.parse(trimmedText);
        } catch (parseError) {
            console.warn('Failed to parse response as JSON:', trimmedText.substring(0, 100));
            return null;
        }
    } catch (err) {
        console.error(`SupabaseFetch Error [${url}]:`, err);
        throw err;
    }
};

/**
 * Helper for 'checkSession' replacement
 * Returns the session object structure if valid token exists locally
 */
export const checkSession = () => {
    const token = getAuthTokenSync();
    if (!token) return null;

    // We can't easily verify validity without a network call,
    // but we can return the structure expected by the app.
    // Ideally we would hit /auth/v1/user to verify, but let's trust local first for speed
    // and let data calls fail if 401.

    try {
        const storageKey = 'instacredmoney-auth';
        const sessionStr = window.localStorage.getItem(storageKey);
        if (sessionStr) {
            const sessionData = JSON.parse(sessionStr);
            // Return structure mimicking Supabase SDK session
            return {
                access_token: token,
                user: sessionData.user || {}
            };
        }
    } catch (e) {
        return null;
    }
    return null;
};

// Export getAuthToken for use in other services
export { getAuthToken };
